// Pure state for the Apps tab (pages/container-tabs/integrations/Bundles.tsx):
// which action a bundle offers, what its status line and update marker say,
// which versions its detail sheet lists, how categories are labelled and
// ordered, how built-in capabilities turn off, and where a web address stands.
// No React here, so `node --test` covers it.
//
// A Boo that reconciles its bundles reports a phase for each one
// (`install.reconcile`): those rows read from the phase instead of the
// install operations, and never offer a manual update.

import type { BundleCatalogEntry, BundleInstallStatus, BundleReconcileStatus } from "./api.ts";

export const INSTALL_IN_FLIGHT = new Set<BundleInstallStatus>(["pending", "installing", "uninstalling", "upgrading"]);
// Rows listed under "On this Boo"; everything else is under "Available".
export const ON_BOO = new Set<BundleInstallStatus>(["installed", "upgrading", "uninstalling"]);

// The Apps tab's sub-tabs, picked by `?tab=`.
export const APPS_TABS = ["store", "configuration"] as const;
export type AppsSubTab = (typeof APPS_TABS)[number];

// Built into the Boo, per its manifest: listed as built-in capabilities under
// Advanced, never in the App store. Brains that don't send `core` keep the
// App store as it was, with no built-in capabilities.
export const isCoreBundle = (bundle: Pick<BundleCatalogEntry, "core">) => bundle.core === true;

const requiresOf = (bundle: Pick<BundleCatalogEntry, "requires">): string[] => bundle.requires ?? [];

// A built-in capability that can't be turned off.
export const alwaysOn = (bundle: Pick<BundleCatalogEntry, "turnOff">) => bundle.turnOff?.allowed === false;

// The turn-off confirm's body, from the manifest: what the Boo loses, then
// what happens to what it stored when the manifest says.
export function turnOffBody(bundle: Pick<BundleCatalogEntry, "name" | "turnOff">): string {
  const loses = bundle.turnOff?.loses?.trim() || `Your Boo won't be able to use ${bundle.name} until you turn it back on.`;
  const data =
    bundle.turnOff?.data === "kept"
      ? "Anything it stored is kept and comes back when you turn it on again."
      : bundle.turnOff?.data === "deleted"
        ? "Everything it stored is deleted for good."
        : null;
  return data ? `${loses} ${data}` : loses;
}

// The bundles on the Boo that need `id`; it can't be removed while any is.
export const dependentsOn = (id: string, rows: BundleCatalogEntry[]): BundleCatalogEntry[] =>
  rows.filter((b) => b.id !== id && ON_BOO.has(b.install.status) && requiresOf(b).includes(id));

// The App store's word for taking an app off the Boo (its detail sheet's
// button), which the built-in capabilities quote when an app holds one on.
export const STORE_REMOVE_VERB = "Disable";

// An installed app still on its temporary address: its own web address (on
// the Boo's domain) didn't attach. The temporary one is never linked.
export const addressPending = (b: BundleCatalogEntry) =>
  b.install.status === "installed" && b.install.domainAttached === false;

// An installed app whose address is being attached right now, by anyone.
export const addressAttaching = (b: BundleCatalogEntry) =>
  b.install.status === "installed" && b.install.domainAttaching === true;

// Where a "Try again" on web addresses stands, given the rows it waited on
// (id → the error they had) and the latest list. Waiting while any of them is
// still attaching; then ready if all attached, failed if any is still pending.
// A pending row that hasn't been seen attaching counts as failed only on a new
// error or, when the list carries `domainAttaching`, once `graceOver` (the
// attach had time to start); `seen` collects rows seen attaching across polls.
export function settleAddresses(
  waiting: ReadonlyMap<string, string | null>,
  rows: BundleCatalogEntry[],
  seen: Set<string>,
  graceOver: boolean,
): "ready" | "failed" | "waiting" {
  let open = 0;
  let failed = false;
  for (const [id, prevError] of waiting) {
    const row = rows.find((b) => b.id === id);
    if (!row) continue;
    if (addressAttaching(row)) {
      seen.add(id);
      open++;
    } else if (addressPending(row)) {
      const tracked = row.install.domainAttaching !== undefined;
      if (seen.has(id) || row.install.domainError !== prevError || (tracked && graceOver)) failed = true;
      else open++;
    }
  }
  if (open) return "waiting";
  return failed ? "failed" : "ready";
}

// A built-in capability that was never on this Boo (older Boos don't get new
// ones on their own), as opposed to one somebody turned off: the backend
// records an uninstall as "uninstalled" and never-installed as "not_installed".
export const notOnBooYet = (b: BundleCatalogEntry) => b.install.status === "not_installed";

// A built-in capability's switch reads on while it is on the Boo or on its way.
export const capabilityOn = (status: BundleInstallStatus) =>
  status === "installed" || status === "upgrading" || status === "pending" || status === "installing";

export const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const reconcileOf = (b: BundleCatalogEntry): BundleReconcileStatus | null => b.install.reconcile ?? null;

// A Retry or Repair the Boo hasn't picked up yet.
export const rerunPending = (b: BundleCatalogEntry): boolean => {
  const r = reconcileOf(b);
  return r !== null && (b.install.rerun ?? 0) > r.rerunSeen;
};

const updatingVersion = (r: BundleReconcileStatus): string | null =>
  r.installedVersion !== undefined && r.targetVersion !== undefined && r.installedVersion !== r.targetVersion
    ? r.targetVersion
    : null;

// How soon the list should look again for a reconciled row: while it runs,
// soon; while it waits or retries later, now and then; otherwise not at all.
export function reconcilePoll(b: BundleCatalogEntry): "soon" | "later" | null {
  const r = reconcileOf(b);
  if (!r) return null;
  if (rerunPending(b) || r.phase === "progressing") return "soon";
  if (r.phase === "retrying" || r.phase === "waiting") return "later";
  return null;
}

// How soon to look again for the whole list. A reconciled row reads from its
// phase alone (its install status says "installing" for days while it waits);
// any other row from its operation and its address attach.
export function listPoll(rows: BundleCatalogEntry[]): "soon" | "later" | null {
  let later = false;
  for (const b of rows) {
    const poll = reconcileOf(b) ? reconcilePoll(b) : INSTALL_IN_FLIGHT.has(b.install.status) || addressAttaching(b) ? "soon" : null;
    if (poll === "soon") return "soon";
    if (poll === "later") later = true;
  }
  return later ? "later" : null;
}

// The toast for a reconciled row whose phase just changed, named the way the
// row reads: Enabled or Updated when work settles, an error when it failed.
export function reconcileAnnouncement(
  prev: BundleReconcileStatus | null | undefined,
  bundle: BundleCatalogEntry,
): { tone: "success" | "error"; text: string } | null {
  const r = reconcileOf(bundle);
  if (!r || !prev || prev.phase === r.phase) return null;
  const wasInstalled = prev.installedVersion !== undefined;
  if (r.phase === "failed") {
    const doing = r.desired === "absent" ? "disable" : wasInstalled ? "update" : "enable";
    return { tone: "error", text: `Couldn't ${doing} ${bundle.name}` };
  }
  // A quick action can finish between two polls, so any unsettled phase that
  // settles counts; a failed one the user retried recovers on its own row.
  if (r.phase !== "converged" || prev.phase === "failed") return null;
  if (r.desired === "absent") return { tone: "success", text: `Disabled ${bundle.name}` };
  if (!wasInstalled) return { tone: "success", text: `Enabled ${bundle.name}` };
  return prev.installedVersion !== r.installedVersion ? { tone: "success", text: `Updated ${bundle.name}` } : null;
}

const WAITING_ON: Record<string, string> = {
  "domain-unconfigured": "Waiting for this Boo's web address to be set up.",
  dependencies: "Waiting for the apps it needs to finish setting up.",
  dependents: "Waiting for the apps that use it to be turned off.",
  credentials: "Waiting for this Boo's web address service to be ready.",
  platform: "Waiting for app hosting to be set up on this Boo.",
  inputs: "Waiting for details it needs.",
};

// "in 4 min", "in under a minute", or "shortly" when the time is unknown or past.
export function retryIn(nextAttemptAt: string | undefined, now: number): string {
  const at = nextAttemptAt ? Date.parse(nextAttemptAt) : Number.NaN;
  if (Number.isNaN(at) || at <= now) return "shortly";
  const minutes = Math.round((at - now) / 60_000);
  return minutes < 1 ? "in under a minute" : `in ${minutes} min`;
}

// Labels for the categories the brain ships today. The brain owns the list, so
// an unknown category still shows, under its own name.
const CATEGORY_LABEL: Record<string, string> = {
  research: "Research",
  data: "Data",
  content: "Content",
  productivity: "Productivity",
  operations: "Operations",
  security: "Security",
};
const CATEGORY_ORDER = Object.keys(CATEGORY_LABEL);

export const categoryLabel = (c: string) =>
  CATEGORY_LABEL[c] ?? (c.charAt(0).toUpperCase() + c.slice(1)).replace(/[-_]+/g, " ");

// Known categories in their fixed order, then any others alphabetically.
export function orderCategories(categories: Iterable<string>): string[] {
  const rank = (c: string) => (CATEGORY_ORDER.includes(c) ? CATEGORY_ORDER.indexOf(c) : CATEGORY_ORDER.length);
  return [...new Set(categories)].sort((a, b) => rank(a) - rank(b) || categoryLabel(a).localeCompare(categoryLabel(b)));
}

// The brain offers an update when the installed content differs from the
// image's, which can happen without a version bump. Name the version only
// when it actually differs.
export const newVersion = (bundle: BundleCatalogEntry): string | null =>
  bundle.version !== bundle.install.upgrade.installedVersion ? bundle.version : null;

// What a bundle's single action button does. The button carries the state, so
// nothing shows a status badge next to a button that says the same thing.
export type RowAction =
  | { kind: "enable" }
  | { kind: "retry" } // a reconciled bundle failed, or is retrying later
  | { kind: "repair" } // a reconciled app isn't running
  | { kind: "waiting" } // a reconciled bundle waits on something outside it
  | { kind: "add-details" } // a reconciled bundle waits on inputs only a person can give
  | { kind: "retry-install" }
  | { kind: "update"; version: string | null }
  | { kind: "retry-update"; version: string | null }
  | { kind: "enabled" }
  | { kind: "busy"; label: string };

export function rowAction(bundle: BundleCatalogEntry, pendingLabel: string | null): RowAction {
  const { status, upgrade } = bundle.install;
  if (pendingLabel) return { kind: "busy", label: pendingLabel };
  const r = reconcileOf(bundle);
  if (r) return reconciledAction(bundle, r);
  if (status === "pending" || status === "installing") return { kind: "busy", label: "Enabling…" };
  if (status === "upgrading") return { kind: "busy", label: "Updating…" };
  if (status === "uninstalling") return { kind: "busy", label: "Disabling…" };
  if (status === "error") return { kind: "retry-install" };
  if (status === "installed") {
    if (upgrade.available) {
      // After a failed attempt installedVersion already reads as the target,
      // so a retry names the target it snapshotted.
      return upgrade.error || upgrade.unconfirmed
        ? { kind: "retry-update", version: upgrade.target }
        : { kind: "update", version: newVersion(bundle) };
    }
    return { kind: "enabled" };
  }
  return { kind: "enable" };
}

function reconciledAction(bundle: BundleCatalogEntry, r: BundleReconcileStatus): RowAction {
  if (rerunPending(bundle)) return { kind: "busy", label: "Retrying…" };
  const installed = r.installedVersion !== undefined;
  if (r.desired === "absent") {
    if (r.phase === "progressing" && installed) return { kind: "busy", label: "Disabling…" };
    if (r.phase === "failed" || r.phase === "retrying") return { kind: "retry" };
    if (r.phase === "waiting" && installed) return { kind: "waiting" };
    return { kind: "enable" };
  }
  switch (r.phase) {
    case "progressing":
      return { kind: "busy", label: updatingVersion(r) ? "Updating…" : installed ? "Working…" : "Enabling…" };
    case "retrying":
    case "failed":
      return { kind: "retry" };
    case "degraded":
      return { kind: "repair" };
    case "waiting":
      if (r.lastError?.stage === "inputs" && bundle.inputs.some((input) => input.required)) return { kind: "add-details" };
      return installed ? { kind: "enabled" } : { kind: "waiting" };
    case "converged":
      return { kind: "enabled" };
    default:
      return installed ? { kind: "enabled" } : { kind: "waiting" };
  }
}

// One line of state for a tile or the detail sheet, only when there is
// something to say beyond the update marker. `now` places a retry in time.
export function statusMessage(bundle: BundleCatalogEntry, now: number = Date.now()): { tone: "muted" | "error"; text: string } | null {
  const r = reconcileOf(bundle);
  if (r) return reconciledMessage(bundle, r, now);
  const { status, error, stage, missingInputs, upgrade } = bundle.install;
  if (status === "upgrading") {
    return { tone: "muted", text: "Updating. This can take a few minutes." };
  }
  if (status === "error") {
    if (missingInputs.length) return { tone: "error", text: "Some required details were missing. Retry to fill them in." };
    if (stage === "source-host-repository") {
      return { tone: "error", text: `${error ?? "That repository already exists."} Retry and choose another name.` };
    }
    return { tone: "error", text: `Couldn't enable: ${[stage, error].filter(Boolean).join(": ") || "unknown error"}` };
  }
  if (status === "installed" && upgrade.available) {
    if (upgrade.unconfirmed) {
      return { tone: "muted", text: "The update hasn't confirmed it finished. Check back in a few minutes." };
    }
    if (missingInputs.length) return { tone: "error", text: "This version needs more details. Retry the update to fill them in." };
    if (upgrade.error) return { tone: "error", text: `The update didn't finish: ${upgrade.error}` };
    return null;
  }
  if (status === "installed" && upgrade.conflicts.length) {
    return {
      tone: "muted",
      text: `Updated. Boo is reconciling ${plural(upgrade.conflicts.length, "customized area", "customized areas")}.`,
    };
  }
  return null;
}

function reconciledMessage(
  bundle: BundleCatalogEntry,
  r: BundleReconcileStatus,
  now: number,
): { tone: "muted" | "error"; text: string } | null {
  if (rerunPending(bundle)) return null;
  const installed = r.installedVersion !== undefined;
  const reason = r.lastError?.reason;
  const doing = r.desired === "absent" ? "disable" : installed ? "update" : "enable";
  switch (r.phase) {
    case "progressing": {
      const next = updatingVersion(r);
      return next ? { tone: "muted", text: `Updating to ${next}. This can take a few minutes.` } : null;
    }
    case "retrying":
      return {
        tone: "muted",
        text: `Couldn't ${doing} yet${reason ? `: ${reason}` : ""}. Trying again ${retryIn(r.nextAttemptAt, now)}.`,
      };
    case "waiting":
      return {
        tone: "muted",
        text: WAITING_ON[r.lastError?.stage ?? ""] ?? "Waiting for its setup to finish.",
      };
    case "failed":
      return { tone: "error", text: `Couldn't ${doing}${reason ? `: ${reason}` : "."}` };
    case "degraded":
      return { tone: "error", text: "Not running right now. Repair to bring it back." };
    case "converged": {
      const conflicts = r.upgradeConflicts ?? [];
      return conflicts.length
        ? { tone: "muted", text: `Updated. Boo is reconciling ${plural(conflicts.length, "customized area", "customized areas")}.` }
        : null;
    }
    default:
      return null;
  }
}

// The marker beside an installed bundle's name while an update is pending.
// The row names only the new version; the sheet shows installed and new.
export function updateMarker(
  bundle: BundleCatalogEntry,
): { tone: "update" | "failed" | "confirming"; label: string } | null {
  const r = reconcileOf(bundle);
  if (r) return reconciledMarker(bundle, r);
  const { status, upgrade, missingInputs } = bundle.install;
  if (status !== "installed" || !upgrade.available) return null;
  if (upgrade.unconfirmed) return { tone: "confirming", label: "Confirming update" };
  if (upgrade.error || missingInputs.length) return { tone: "failed", label: "Update failed" };
  const next = newVersion(bundle);
  return { tone: "update", label: next ? `New version ${next}` : "Update available" };
}

// On a reconciled row: what's wrong with an installed bundle, if anything.
function reconciledMarker(
  bundle: BundleCatalogEntry,
  r: BundleReconcileStatus,
): { tone: "update" | "failed" | "confirming"; label: string } | null {
  if (r.installedVersion === undefined || r.desired === "absent" || rerunPending(bundle)) return null;
  switch (r.phase) {
    case "failed":
      return { tone: "failed", label: updatingVersion(r) ? "Update failed" : "Failed" };
    case "degraded":
      return { tone: "failed", label: "Not running" };
    case "retrying":
      return { tone: "confirming", label: "Retrying" };
    case "waiting":
      return { tone: "confirming", label: "Waiting" };
    default:
      return null;
  }
}

// The detail sheet's version rows: what's installed, and what an update
// brings when the number changes.
export function versionRows(bundle: BundleCatalogEntry): [label: string, value: string][] {
  const r = reconcileOf(bundle);
  if (r) {
    if (r.installedVersion === undefined) return [["Version", r.targetVersion ?? bundle.version]];
    const next = updatingVersion(r);
    return next ? [["Version", r.installedVersion], ["Updating to", next]] : [["Version", r.installedVersion]];
  }
  const { status, upgrade } = bundle.install;
  if (!ON_BOO.has(status)) return [["Version", bundle.version]];
  // During or after a failed upgrade installedVersion already reads as the
  // target, so there is no trustworthy current version to show.
  if (status !== "installed" || upgrade.error || upgrade.unconfirmed) return [];
  const rows: [string, string][] = [];
  const current = upgrade.installedVersion ?? (upgrade.available ? null : bundle.version);
  if (current) rows.push(["Version", current]);
  const next = upgrade.available ? newVersion(bundle) : null;
  if (next) rows.push(["New version", next]);
  return rows;
}

// Updates ready to start: failed, unconfirmed and input-blocked ones say so on
// their own row instead.
export const countPendingUpdates = (bundles: BundleCatalogEntry[]) =>
  bundles.filter(
    (b) =>
      !reconcileOf(b) &&
      b.install.status === "installed" &&
      b.install.upgrade.available &&
      !b.install.upgrade.error &&
      !b.install.upgrade.unconfirmed &&
      !b.install.missingInputs.length,
  ).length;
